import { useEffect, useMemo, useState } from "react";
import { FiEdit2, FiPlus, FiSearch, FiTrash2 } from "react-icons/fi";
import api from "../services/api";

const PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];

const FAQs = () => {
  const [faqs, setFaqs] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    question: "",
    answer: "",
    is_active: 1,
  });

  const totalPages = useMemo(() => {
    return lastPage || Math.max(1, Math.ceil(total / perPage));
  }, [lastPage, total, perPage]);

  const fetchFaqs = async () => {
    setIsLoading(true);
    setSubmitErrors([]);
    setSubmitSuccess("");

    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setSubmitErrors(["Missing access token."]);
        return;
      }

      const response = await api.get("/faqs", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          search: query,
          sort_by: sortBy,
          sort_dir: sortDir,
          page,
          per_page: perPage,
        },
      });

      if (response.data?.status !== "success") {
        setSubmitErrors(["Failed to load FAQs."]);
        return;
      }

      const data = response.data?.data || {};
      setFaqs(data.rows || []);
      setTotal(data.total || 0);
      setLastPage(data.last_page || 1);
    } catch (error) {
      setSubmitErrors(["Failed to load FAQs."]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, [query, sortBy, sortDir, page, perPage]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
  };

  const handleOpenModal = (mode, faq = null) => {
    setModalMode(mode);
    setEditingId(faq?.id || null);
    setFormData({
      question: faq?.question || "",
      answer: faq?.answer || "",
      is_active: mode === "create" ? 1 : faq?.is_active ? 1 : 0,
    });
    setSubmitErrors([]);
    setSubmitSuccess("");
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "is_active" ? Number(value) : value,
    }));
  };

  const handleSaveFaq = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setSubmitErrors([]);
    setSubmitSuccess("");

    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setSubmitErrors(["Missing access token."]);
        return;
      }

      const payload = {
        question: formData.question,
        answer: formData.answer,
        is_active: Number(formData.is_active),
      };

      const request = modalMode === "create"
        ? api.post("/faqs", payload, {
            headers: { Authorization: `Bearer ${token}` },
          })
        : api.put(`/faqs/${editingId}`, payload, {
            headers: { Authorization: `Bearer ${token}` },
          });

      const response = await request;
      if (response.data?.status !== "success") {
        const message = response.data?.message || "Failed to save FAQ.";
        if (message && typeof message === "object") {
          setSubmitErrors(Object.values(message).flat());
        } else {
          setSubmitErrors([message]);
        }
        return;
      }

      setSubmitSuccess(response.data?.message || "FAQ saved.");
      setIsModalOpen(false);
      fetchFaqs();
    } catch (error) {
      const message = error?.response?.data?.message;
      if (message && typeof message === "object") {
        setSubmitErrors(Object.values(message).flat());
      } else if (typeof message === "string") {
        setSubmitErrors([message]);
      } else {
        setSubmitErrors(["Failed to save FAQ."]);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async (faq) => {
    setSubmitErrors([]);
    setSubmitSuccess("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setSubmitErrors(["Missing access token."]);
        return;
      }

      const response = await api.patch(
        `/faqs/${faq.id}/status`,
        { is_active: faq.is_active ? 0 : 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.status !== "success") {
        setSubmitErrors(["Failed to update status."]);
        return;
      }

      fetchFaqs();
    } catch (error) {
      setSubmitErrors(["Failed to update status."]);
    }
  };

  const handleDelete = async (faq) => {
    const confirmed = window.confirm(
      "This action cannot be undone. Delete this FAQ?"
    );
    if (!confirmed) {
      return;
    }

    setSubmitErrors([]);
    setSubmitSuccess("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setSubmitErrors(["Missing access token."]);
        return;
      }

      const response = await api.delete(`/faqs/${faq.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.status !== "success") {
        setSubmitErrors(["Failed to delete FAQ."]);
        return;
      }

      fetchFaqs();
    } catch (error) {
      setSubmitErrors(["Failed to delete FAQ."]);
    }
  };

  const renderPageButtons = () => {
    const buttons = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + 4);

    for (let p = start; p <= end; p += 1) {
      buttons.push(
        <button
          key={p}
          type="button"
          onClick={() => setPage(p)}
          className={`min-w-[40px] rounded-xl border px-3 py-2 text-sm font-semibold ${
            p === page
              ? "border-[var(--brand-orange)] bg-orange-50 text-[var(--brand-orange-dark)]"
              : "border-slate-200 text-slate-600 hover:border-slate-300"
          }`}
        >
          {p}
        </button>
      );
    }

    return buttons;
  };

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
              Support
            </p>
            <h1 className="mt-2 text-3xl font-semibold text-slate-900">
              FAQs Management
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Manage frequently asked questions shown to users.
            </p>
          </div>
          <button
            type="button"
            onClick={() => handleOpenModal("create")}
            className="inline-flex items-center gap-2 rounded-2xl bg-[var(--brand-orange)] px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
          >
            <FiPlus />
            Add New FAQs
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <form onSubmit={handleSearchSubmit} className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm">
              <FiSearch />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search question or answer"
                className="w-64 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
            >
              Search
            </button>
          </form>

          <div className="flex items-center gap-3 text-sm text-slate-500">
            <span>Display</span>
            <select
              value={perPage}
              onChange={(event) => {
                setPerPage(Number(event.target.value));
                setPage(1);
              }}
              className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              {PER_PAGE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        </div>

        {submitErrors.length > 0 && (
          <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            <ul className="space-y-1">
              {submitErrors.map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </ul>
          </div>
        )}
        {submitSuccess && (
          <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-600">
            {submitSuccess}
          </div>
        )}

        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full text-left text-sm text-slate-600">
            <thead className="text-xs uppercase text-slate-400">
              <tr>
                {[
                  { key: "id", label: "ID" },
                  { key: "question", label: "Question" },
                  { key: "answer", label: "Answer" },
                  { key: "oauth_user_id", label: "OAuth User ID" },
                  { key: "is_active", label: "Active" },
                  { key: "created_at", label: "Created At" },
                  { key: "updated_at", label: "Updated At" },
                ].map((column) => (
                  <th
                    key={column.key}
                    className="cursor-pointer px-4 py-3 text-xs font-semibold"
                    onClick={() => handleSort(column.key)}
                  >
                    <div className="flex items-center gap-2">
                      {column.label}
                      {sortBy === column.key && (
                        <span className="text-[var(--brand-orange)]">
                          {sortDir === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-4 py-3 text-xs font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="px-4 py-6 text-center text-slate-400" colSpan={8}>
                    Loading FAQs...
                  </td>
                </tr>
              ) : faqs.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-center text-slate-400" colSpan={8}>
                    No FAQs found.
                  </td>
                </tr>
              ) : (
                faqs.map((faq) => (
                  <tr key={faq.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-semibold text-slate-700">
                      {faq.id}
                    </td>
                    <td className="px-4 py-3 max-w-[260px] break-words">
                      {faq.question}
                    </td>
                    <td className="px-4 py-3 max-w-[320px] break-words">
                      {faq.answer}
                    </td>
                    <td className="px-4 py-3">{faq.oauth_user_id}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          faq.is_active
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {faq.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3">{faq.created_at}</td>
                    <td className="px-4 py-3">{faq.updated_at}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleOpenModal("edit", faq)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                        >
                          <FiEdit2 />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(faq)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                        >
                          {faq.is_active ? "Deactivate" : "Activate"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(faq)}
                          className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:text-rose-700"
                        >
                          <FiTrash2 />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
          <div>
            Showing {faqs.length} of {total} entries
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              First
            </button>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Previous
            </button>
            {renderPageButtons()}
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page === totalPages}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  FAQs
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  {modalMode === "create" ? "Add New FAQ" : "Edit FAQ"}
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Provide the question and the answer for the FAQ entry.
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <form onSubmit={handleSaveFaq} className="mt-6 space-y-4">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Question
                </label>
                <textarea
                  name="question"
                  value={formData.question}
                  onChange={handleFormChange}
                  rows={3}
                  placeholder="Enter the question"
                  className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Answer
                </label>
                <textarea
                  name="answer"
                  value={formData.answer}
                  onChange={handleFormChange}
                  rows={5}
                  placeholder="Enter the answer"
                  className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Status
                </label>
                <select
                  name="is_active"
                  value={formData.is_active}
                  onChange={handleFormChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
                >
                  {isSaving ? "Saving..." : "Save FAQ"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQs;
